import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Lock, Sparkles } from "lucide-react";

export default function LeanWeek() {
  const { n } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/lean/week/${n}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load week"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [n]);

  const logWorkout = async (letter) => {
    setBusy(letter);
    try {
      const { data: res } = await api.post("/lean/log/workout", { week: parseInt(n), letter });
      toast.success(`Workout ${letter} logged (${res.workouts_logged}/${res.workouts_target} this week).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log workout."); }
    finally { setBusy(null); }
  };
  const unlogWorkout = async (letter) => {
    setBusy(letter);
    try {
      await api.delete(`/lean/log/workout?week=${n}&letter=${letter}`);
      toast.success(`Workout ${letter} unlogged.`);
      load();
    } catch (e) { toast.error("Could not unlog workout."); }
    finally { setBusy(null); }
  };

  const complete = async () => {
    try {
      const { data: res } = await api.post(`/lean/week/${n}/complete`);
      toast.success(res.block_complete ? "LEAN block complete." : `Week ${n} marked complete.`);
      if (res.phase_transition) {
        setTimeout(() => alert(res.phase_transition.message), 400);
      }
      nav("/app/lean");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete week");
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div>Week not found.</div>;

  if (!data.unlocked) {
    return (
      <div className="max-w-xl">
        <Link to="/app/lean" className="text-sm text-muted-foreground flex items-center gap-1"><ArrowLeft size={14}/> LEAN Dashboard</Link>
        <div className="mt-6 bg-card border border-border rounded-lg p-8 text-center" data-testid="week-locked">
          <Lock className="mx-auto text-muted-foreground" size={28}/>
          <h2 className="mt-3 font-head uppercase text-2xl">Week {n} Locked</h2>
          <p className="mt-2 text-sm text-muted-foreground">Complete earlier weeks to unlock this one.</p>
        </div>
      </div>
    );
  }

  const { week, phase, phase_color, phase_purpose, track, curriculum, workouts, mvw, completed } = data;

  return (
    <div className="space-y-6 max-w-4xl">
      <Link to="/app/lean" className="text-sm text-muted-foreground flex items-center gap-1"><ArrowLeft size={14}/> LEAN Dashboard</Link>

      <div>
        <div className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 font-head uppercase tracking-widest text-xs text-white" style={{background: phase_color}}>
          {phase} · Week {week} · {track}
        </div>
        <h1 className="wordmark mt-4 text-4xl md:text-6xl uppercase leading-[0.9]">{curriculum.title}.</h1>
        <p className="mt-3 text-sm text-foreground/75 max-w-2xl">{phase_purpose}</p>
      </div>

      {/* Lesson */}
      <div className="bg-foreground text-background rounded-lg p-6" data-testid="week-lesson">
        <div className="flex items-center gap-2 label-eyebrow !text-background/70"><Sparkles size={12}/> Weekly Lesson</div>
        <p className="mt-3 font-head uppercase text-xl md:text-2xl leading-snug">{curriculum.lesson}</p>
      </div>

      {/* Targets grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card label="Train"   text={curriculum.train}   testid="target-train" />
        <Card label="Walk"    text={curriculum.walk}    testid="target-walk" />
        <Card label="Eat"     text={curriculum.eat}     testid="target-eat" />
        <Card label="Recover" text={curriculum.recover} testid="target-recover" />
      </div>

      {/* Adjustment rule */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="week-rule">
        <div className="label-eyebrow" style={{color: phase_color}}>Adjustment Rule</div>
        <p className="mt-2 text-sm font-head uppercase text-lg md:text-xl leading-snug">{curriculum.rule}</p>
      </div>

      {/* Workouts A/B/C */}
      <div>
        <div className="label-eyebrow" style={{color: phase_color}}>Train — {track} · Workout A/B/C</div>
        <div className="mt-4 grid gap-4">
          {workouts.map((w) => (
            <div key={w.letter} className="bg-card border border-border rounded-lg p-6" data-testid={`workout-${w.letter}`}>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="font-head uppercase text-xl">Workout {w.letter}</div>
                  {w.logged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-head uppercase tracking-widest"
                      data-testid={`workout-${w.letter}-logged-badge`}>
                      ✓ Logged {w.logged_at}
                    </span>
                  )}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{track}</div>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{w.focus}</p>
              {w.image && (
                <a href={w.image} target="_blank" rel="noreferrer" className="mt-4 block rounded-md overflow-hidden border border-border" data-testid={`workout-${w.letter}-image`}>
                  <img src={w.image} alt={`BA30 LEAN ${phase} Workout ${w.letter} — ${track} track illustrations`} loading="lazy" className="block w-full h-auto" />
                </a>
              )}
              <table className="mt-4 w-full text-sm">
                <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left py-2">Exercise</th>
                    <th className="text-center py-2">Sets</th>
                    <th className="text-center py-2">Reps</th>
                    <th className="text-center py-2">RIR</th>
                  </tr>
                </thead>
                <tbody>
                  {w.exercises.map((ex, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0">
                      <td className="py-2.5 pr-2">
                        <div className="font-medium">{ex.name}</div>
                        {ex.coaching && <div className="text-[11px] text-muted-foreground mt-0.5">{ex.coaching}</div>}
                      </td>
                      <td className="text-center py-2.5">{ex.sets}</td>
                      <td className="text-center py-2.5">{ex.reps}</td>
                      <td className="text-center py-2.5">{ex.rir}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Log / Unlog Workout button */}
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                {!w.logged ? (
                  <button onClick={() => logWorkout(w.letter)} disabled={busy===w.letter}
                    className="btn-primary" data-testid={`log-workout-${w.letter}`}>
                    {busy===w.letter ? "Logging…" : `Log Workout ${w.letter}`}
                  </button>
                ) : (
                  <button onClick={() => unlogWorkout(w.letter)} disabled={busy===w.letter}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                    data-testid={`unlog-workout-${w.letter}`}>
                    Undo log
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground" data-testid="week-workouts-progress">
          {data.workouts_logged ?? 0} of {data.workouts_target ?? 3} sessions logged this week
        </div>
      </div>

      {/* Week 11 MVW */}
      {mvw && (
        <div className="bg-card border border-primary/30 rounded-lg p-6" data-testid="week-mvw">
          <div className="label-eyebrow text-primary">Minimum Viable LEAN Week (Optional)</div>
          <p className="mt-2 text-sm text-foreground/75">If life or fatigue genuinely prevents three sessions, use two condensed full-body sessions rather than disappearing.</p>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {["session_1","session_2"].map((k, i) => (
              <div key={k} className="border border-border rounded-md p-4">
                <div className="font-head uppercase text-sm">Session {i+1}</div>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {mvw[k].map((ex, j) => (
                    <li key={j} className="flex justify-between gap-2">
                      <span>{ex.name}</span>
                      <span className="text-xs text-muted-foreground">{ex.sets}×{ex.reps} · {ex.rir} RIR</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review row */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="week-review">
        <div className="label-eyebrow" style={{color: phase_color}}>Weekly Review</div>
        <p className="mt-2 text-sm text-foreground/75">{curriculum.review}</p>
        {completed ? (
          <div className="mt-4 inline-flex items-center gap-2 text-emerald-600 text-sm">
            <CheckCircle2 size={16}/> Week {week} complete
          </div>
        ) : (
          <button onClick={complete} className="btn-primary mt-4" data-testid="week-complete-btn">
            Complete Week {week}
          </button>
        )}
      </div>
    </div>
  );
}

function Card({ label, text, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <p className="mt-2 text-sm text-foreground/80">{text}</p>
    </div>
  );
}
